import React from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";

export const PLAN_LABEL = { free: "Free", basic: "Basic", pro: "Pro", ai_manager: "AI Manager" };
export const PLAN_BADGE = {
  free:  "bg-neutral-100 text-neutral-700 border-neutral-200",
  basic: "bg-neutral-100 text-neutral-800 border-neutral-200",
  pro:   "bg-primary/15 text-neutral-900 border-primary/30",
  ai_manager: "bg-neutral-900 text-white border-neutral-900",
};

export const BILLING_REASON_LABEL = {
  subscription_create: "Primo pagamento",
  subscription_cycle: "Rinnovo",
  subscription_update: "Aggiornamento",
  subscription_threshold: "Soglia utilizzo",
  manual: "Manuale",
  upcoming: "Prossimo addebito",
};

export function eventTypeLabel(row) {
  if (row?.status === "failed") return "Pagamento fallito";
  return BILLING_REASON_LABEL[row?.billing_reason] || (row?.billing_reason ? row.billing_reason : "Pagamento");
}

export function formatEUR(n, dec = 2) {
  return `${(Number(n) || 0).toFixed(dec).replace(".", ",")} €`;
}
const MONTHS_IT = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];
export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS_IT[d.getMonth()]} ${d.getFullYear()}`;
}
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${formatDate(iso)}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}
export function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "adesso";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const g = Math.floor(h / 24);
  if (g < 30) return `${g} ${g === 1 ? "giorno" : "giorni"} fa`;
  return formatDate(iso);
}
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}
export function initials(name, email) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "—";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "—";
}
export function cycleLabel(u) {
  const prov = (u?.billing_provider || "").toLowerCase();
  if (prov === "manual" || prov === "internal" || u?.origin === "gift" || u?.origin === "manual") {
    return u?.manual_grant_duration === "one_month" ? "1 mese"
      : u?.manual_grant_duration === "one_year" ? "1 anno"
      : u?.manual_grant_duration === "permanent" ? "Senza scadenza" : "Manuale";
  }
  return u?.billing_cycle === "yearly" ? "Annuale" : u?.billing_cycle === "monthly" ? "Mensile" : "—";
}

// ─── Tone system: verde=ok · rosso=errore · arancione=attenzione · grigio=neutro
export const TONE = {
  ok:      "bg-emerald-50 text-emerald-700 border-emerald-100",
  fail:    "bg-rose-50 text-rose-700 border-rose-100",
  warn:    "bg-amber-50 text-amber-800 border-amber-100",
  neutral: "bg-neutral-100 text-neutral-700 border-neutral-200",
  muted:   "bg-neutral-50 text-neutral-400 border-neutral-100",
};
export const DOT = { ok: "bg-emerald-500", fail: "bg-rose-500", warn: "bg-amber-500", neutral: "bg-neutral-400", muted: "bg-neutral-300" };

export function Pill({ tone = "neutral", children, dot = false, className = "", testid }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap ${TONE[tone] || TONE.neutral} ${className}`} data-testid={testid}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${DOT[tone] || DOT.neutral}`} />}
      {children}
    </span>
  );
}

export function StatusPill({ status, suspended, cancelAtPeriodEnd, periodEnd, testid }) {
  if (suspended) return <Pill tone="fail" testid={testid}>Sospeso</Pill>;
  if (status === "active") {
    const d = daysUntil(periodEnd);
    if (cancelAtPeriodEnd) return <Pill tone="warn" testid={testid}>Cancellazione a fine periodo</Pill>;
    if (d !== null && d >= 0 && d <= 7) return <Pill tone="warn" testid={testid}>In scadenza</Pill>;
    return <Pill tone="ok" testid={testid}>Attivo</Pill>;
  }
  if (status === "past_due") return <Pill tone="fail" testid={testid}>Pagamento fallito</Pill>;
  if (status === "canceled") return <Pill tone="neutral" testid={testid}>Cancellato</Pill>;
  if (status === "inactive") return <Pill tone="muted" testid={testid}>Inattivo</Pill>;
  return <span className="text-neutral-300 text-sm">—</span>;
}

export function PaymentStatusPill({ status }) {
  if (status === "paid") return <Pill tone="ok" dot>Pagato</Pill>;
  if (status === "failed") return <Pill tone="fail" dot>Fallito</Pill>;
  return <Pill tone="muted">{status || "—"}</Pill>;
}

export function PlanBadge({ plan }) {
  const k = (plan || "free").toLowerCase();
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wider border uppercase ${PLAN_BADGE[k] || PLAN_BADGE.free}`}>
      {PLAN_LABEL[k] || "Free"}
    </span>
  );
}

export function OriginPill({ origin }) {
  if (origin === "stripe") return <span className="text-xs text-neutral-700">Stripe</span>;
  if (origin === "gift") return <span className="text-xs text-neutral-700">Omaggio</span>;
  if (origin === "manual") return <span className="text-xs text-neutral-700">Manuale</span>;
  return <span className="text-neutral-300 text-xs">—</span>;
}

export function DeliveryPill({ status }) {
  const t = {
    sent:      { label: "Inviata", tone: "warn" },
    delivered: { label: "Consegnata", tone: "ok" },
    delayed:   { label: "Ritardata", tone: "warn" },
    opened:    { label: "Aperta", tone: "ok" },
    clicked:   { label: "Cliccata", tone: "ok" },
    bounced:   { label: "Bounced", tone: "fail" },
    failed:    { label: "Non consegnata", tone: "fail" },
    complained:{ label: "Spam complaint", tone: "fail" },
    no_recipient: { label: "Nessun destinatario", tone: "warn" },
    pending:   { label: "In attesa", tone: "muted" },
    sending:   { label: "In invio", tone: "muted" },
  };
  const p = t[status] || { label: status || "—", tone: "muted" };
  return <Pill tone={p.tone}>{p.label}</Pill>;
}

export function PipelinePill({ label, kind }) {
  const tone = kind === "ok" ? "ok" : kind === "warn" ? "warn" : kind === "fail" ? "fail" : kind === "manual" ? "neutral" : "muted";
  const glyph = { ok: "✓", warn: "⚠", fail: "✕", manual: "—", none: "·" };
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-md border text-xs ${TONE[tone]}`}>
      <span className="font-medium">{label}</span>
      <span className="text-sm">{glyph[kind] || "·"}</span>
    </div>
  );
}

export function Delta({ current, previous, invert = false, suffix = "" }) {
  if (previous === null || previous === undefined || current === null || current === undefined) return null;
  const c = Number(current) || 0, p = Number(previous) || 0;
  if (p === 0 && c === 0) return <span className="text-[11px] text-neutral-400 inline-flex items-center gap-1"><Minus className="w-3 h-3" /> 0%</span>;
  const pct = p === 0 ? 100 : Math.round(((c - p) / p) * 100);
  const up = pct > 0;
  const good = invert ? !up : up;
  const Icon = pct === 0 ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`text-[11px] inline-flex items-center gap-1 ${pct === 0 ? "text-neutral-400" : good ? "text-emerald-700" : "text-rose-700"}`}>
      <Icon className="w-3 h-3" strokeWidth={2} /> {pct > 0 ? "+" : ""}{pct}%{suffix}
    </span>
  );
}

export function Kpi({ icon: Icon, label, value, sub, delta, tone = "neutral", testid, onClick }) {
  const iconTone = { neutral: "bg-neutral-100 text-neutral-700", ok: "bg-emerald-50 text-emerald-700", fail: "bg-rose-50 text-rose-700", warn: "bg-amber-50 text-amber-800", primary: "bg-primary/15 text-primary" };
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`bg-white border border-border rounded-md px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] text-left w-full ${onClick ? "hover:border-primary/40 hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)] transition-[border-color,box-shadow]" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2.5">
        {Icon && <span className={`w-7 h-7 rounded-md inline-flex items-center justify-center ${iconTone[tone] || iconTone.neutral}`}><Icon className="w-3.5 h-3.5" strokeWidth={1.8} /></span>}
        <div className="text-[11px] text-neutral-600 font-medium truncate">{label}</div>
      </div>
      <div className="font-serif-heading text-3xl mt-3 tabular-nums text-neutral-900">{value}</div>
      <div className="mt-1.5 flex items-center gap-2 min-h-[16px]">
        {delta}
        {sub ? <span className="text-[11px] text-neutral-500 truncate">{sub}</span> : null}
      </div>
    </Wrapper>
  );
}

export function SectionCard({ icon: Icon, title, action, children, className = "", testid, padded = true }) {
  return (
    <section className={`bg-white border border-border rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.03)] flex flex-col ${className}`} data-testid={testid}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="w-4 h-4 text-primary shrink-0" strokeWidth={1.6} />}
          <h2 className="font-serif-heading text-lg truncate">{title}</h2>
        </div>
        {action}
      </div>
      <div className={`${padded ? "p-5" : ""} flex-1`}>{children}</div>
    </section>
  );
}

export function LinkAction({ onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 transition-colors" data-testid={testid}>
      {children} <span aria-hidden>→</span>
    </button>
  );
}

export function EmptyState({ children }) {
  return <div className="text-sm text-neutral-400 text-center py-8">{children}</div>;
}

export function Spinner() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-primary animate-spin" strokeWidth={1.5} />
    </div>
  );
}

export function Th({ children, align = "left", className = "" }) {
  return <th className={`py-3 px-4 text-[10px] uppercase tracking-[0.12em] font-medium text-neutral-600 ${align === "right" ? "text-right" : "text-left"} ${className}`}>{children}</th>;
}

export function SortableHeader({ label, sortKey, sort, setSort, align = "left" }) {
  const active = sort.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <th className={`py-3 px-4 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => setSort({ key: sortKey, dir: active && dir === "asc" ? "desc" : "asc" })}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-medium text-neutral-600 hover:text-neutral-900"
      >
        {label}
        {dir === "asc" ? <ArrowUp className="w-3 h-3" strokeWidth={2} /> :
         dir === "desc" ? <ArrowDown className="w-3 h-3" strokeWidth={2} /> :
         <ArrowUpDown className="w-3 h-3 opacity-40" strokeWidth={2} />}
      </button>
    </th>
  );
}

export function Avatar({ name, email, size = "md" }) {
  const cls = size === "lg" ? "w-12 h-12 text-sm" : "w-8 h-8 text-[11px]";
  return (
    <span className={`${cls} rounded-full bg-secondary text-neutral-700 font-medium inline-flex items-center justify-center shrink-0`}>
      {initials(name, email)}
    </span>
  );
}

export function PriorityPill({ p }) {
  const tone = p === "alta" ? "fail" : p === "media" ? "warn" : "ok";
  return <Pill tone={tone}>{p ? p[0].toUpperCase() + p.slice(1) : "—"}</Pill>;
}

export function StateDot({ state, label }) {
  const tone = state === "ok" ? "ok" : state === "fail" ? "fail" : state === "warn" ? "warn" : "muted";
  const txt = label || (state === "ok" ? "Operativo" : state === "fail" ? "Errore" : state === "warn" ? "Attenzione" : "—");
  return <Pill tone={tone} dot>{txt}</Pill>;
}
